import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/agencyPublication'

import PageContext from '@contexts/pageContext'

import * as agencyPublicationActions from '@redux/modules/agencyPublication'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'

import type { AgencyPublicationModel, AgencyPublicationRequestOptions } from '@models/types'

type DeleteAgencyPublicationParams = {
  agencyPublication: DeleteParams<AgencyPublicationModel>,
  dispatch: AppDispatch,
}

const deleteAgencyPublication = (params: DeleteAgencyPublicationParams) => {
  const { dispatch, agencyPublication } = params
  const { deleteAgencyPublication: deleteFn } = agencyPublicationActions

  return dispatch(deleteFn(agencyPublication))
}

type BulkDeleteParams = {
  agency_id: number,
  agency_publication_ids: number,
}

type BulkDeleteAgencyPublications = {
  bulkDeleteParams: BulkDeleteParams,
  dispatch: AppDispatch,
  requestOptions?: AgencyPublicationRequestOptions,
}

const bulkDeleteAgencyPublications = (params: BulkDeleteAgencyPublications) => {
  const { bulkDeleteParams, dispatch, requestOptions } = params
  const { bulkDeleteAgencyPublications: bulkDeleteFn } = agencyPublicationActions

  // @ts-expect-error TS2345
  return dispatch(bulkDeleteFn(bulkDeleteParams, requestOptions))
}

type BulkCreateParams = {
  agencyId: number,
  publicationIds: number[],
}

type BulkCreateAgencyPublications = {
  bulkCreateParams: BulkCreateParams,
  dispatch: AppDispatch,
  requestOptions?: AgencyPublicationRequestOptions,
}

const bulkCreateAgencyPublications = (params: BulkCreateAgencyPublications) => {
  const { bulkCreateParams, dispatch, requestOptions } = params
  const { bulkCreateAgencyPublications: bulkCreateFn } = agencyPublicationActions

  return dispatch(bulkCreateFn(bulkCreateParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyPublicationForm(
  agencyPublication: Partial<AgencyPublicationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyPublicationForm = useForm(
    defaultFormState,
    { entity: agencyPublication, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyPublication.id, agencyPublication.cache_key],
  )

  return {
    ...agencyPublicationForm,
  }
}

export const useRelations = (agencyPublication: Partial<AgencyPublicationModel> = {}) => {
  const { agency_id, publication_id } = agencyPublication || {}

  const { agencies, publications } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id ? agencies[agency_id] || {} : {}
  const publication = publication_id ? publications[publication_id] || {} : {}

  return {
    agency,
    publication,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AgencyPublicationRequestOptions,
}

function useAgencyPublication(initEntity: Partial<AgencyPublicationModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: agencyPublication }: {
    entity: AgencyPublicationModel
  // @ts-expect-error TS2345
  } = useLatestEntity(initEntity, 'agencyPublications')
  const { id } = agencyPublication

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, publication } = useRelations(agencyPublication)

  useReduxAction(
    'agencyPublications',
    'loadAgencyPublication',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.agencyPublications)

  return {
    agency,
    agencyPublication,
    callbacks: {
      addAgencyPublications: () => launchModal({
        callbacks,
        modalKey: 'CreateAgencyPublicationsModal',
        payload: { agencyPublication },
      }),
      bulkCreateAgencyPublications: (
        bulkCreateParams: BulkCreateParams,
        entityOptions?: AgencyPublicationRequestOptions
      ) => (
        bulkCreateAgencyPublications({ bulkCreateParams, dispatch, requestOptions: entityOptions })
      ),
      bulkDeleteAgencyPublications: (
        bulkDeleteParams: BulkDeleteParams,
        entityOptions?: AgencyPublicationRequestOptions
      ) => bulkDeleteAgencyPublications({
        bulkDeleteParams, dispatch, requestOptions: entityOptions,
      }),
      deleteAgencyPublication: () => deleteAgencyPublication({ dispatch, agencyPublication }),
    },
    creating,
    deleting,
    loading,
    publication,
  }
}

export default useAgencyPublication
